/**
 * @fileoverview Unified inbox assignment cell: a single search field that lets the user
 * assign a staging URL to a tag, a variant, or the site-wide (general) bucket.
 *
 * Feeds the shared {@link LookupConfirmField} a heterogeneous {@link PickerItem} union and
 * conveys grouping via ordering + a per-item kind indicator (TagChip / SKU row / globe),
 * never literal section headers. For `/collections/*` paths, tags are surfaced first.
 * @module features/sku-alias/components/UrlAssignmentCell
 */

import { type ReactNode, memo, useCallback, useMemo } from 'react'
import { Globe, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { LookupConfirmField } from '@/shared/components/LookupConfirmField'
import { Money } from '@/shared/components/ui'
import { TagChip, type Tag } from '@/features/tags'
import { searchCatalogProducts } from '@/features/sku-alias/api/needsReview'
import type { Variant } from '@/lib/variantService'
import type { StagingExternalUrl } from '@/features/sku-alias/api/stagingExternalUrls'
import {
  buildPickerItems,
  isCollectionPath,
  toVariantPickerItem,
  type PickerItem,
} from './urlPickerItems'

/** Props for {@link UrlAssignmentCell}. */
export interface UrlAssignmentCellProps {
  url: StagingExternalUrl
  /** Tags available for assignment (full list). */
  tags: Tag[]
  /** Assign the URL to a variant. */
  onVariantSelect: (url: StagingExternalUrl, variant: Variant) => Promise<void> | void
  /** Assign the URL to a tag. */
  onTagSelect: (url: StagingExternalUrl, tagUuid: string) => Promise<void> | void
  /** Assign the URL to the site-wide (general) variant. Absence hides the option. */
  onAssignToGeneral?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  /** Clear/reject the AI variant suggestion. Absence hides the reject control. */
  onClearSuggestion?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  isLoading: boolean
  /** Whether this URL currently carries an AI variant suggestion. */
  hasSuggestion: boolean
  /** Localized placeholder/label for the variant column. */
  variantLabel: string
  /** Localized label for the site-wide option. */
  siteWideLabel: string
  /** Localized "AI suggested match" tooltip text. */
  aiSuggestedMatchLabel: string
  /** Localized confirm/reject aria labels. */
  confirmAriaLabel: string
  rejectAriaLabel: string
  /** Confirm-button icon (spinner while loading, check otherwise). */
  confirmIcon: ReactNode
}

const getVariantLabel = (variant: Variant): string => variant.productName ?? variant.name ?? ''

const getItemLabel = (item: PickerItem): string => {
  switch (item.kind) {
    case 'tag':
      return item.tag.name
    case 'variant':
      return getVariantLabel(item.variant)
    case 'general':
      return ''
  }
}

/**
 * Per-row picker cell. Memoized: it re-renders only when its URL, the tags list, or the
 * loading/suggestion flags change.
 */
export const UrlAssignmentCell = memo<UrlAssignmentCellProps>(function UrlAssignmentCell({
  url,
  tags,
  onVariantSelect,
  onTagSelect,
  onAssignToGeneral,
  onClearSuggestion,
  isLoading,
  hasSuggestion,
  variantLabel,
  siteWideLabel,
  aiSuggestedMatchLabel,
  confirmAriaLabel,
  rejectAriaLabel,
  confirmIcon,
}) {
  const allowGeneral = Boolean(onAssignToGeneral)
  // On a /collections/* URL a tag IS the primary assignment target, so reveal the
  // (short) tag list as soon as the field is focused — no search term required.
  const isCollectionTarget = isCollectionPath(url.canonicalPath)

  const fetchResults = useCallback(
    async (query: string): Promise<PickerItem[]> => {
      const variants = await searchCatalogProducts(query)
      return buildPickerItems(query, {
        tags,
        variants,
        canonicalPath: url.canonicalPath,
        allowGeneral,
      })
    },
    [tags, url.canonicalPath, allowGeneral]
  )

  const handleConfirm = useCallback(
    (item: PickerItem): void => {
      switch (item.kind) {
        case 'tag':
          void onTagSelect(url, item.tag.uuid)
          return
        case 'variant':
          void onVariantSelect(url, item.variant)
          return
        case 'general':
          void onAssignToGeneral?.(url)
          return
      }
    },
    [url, onTagSelect, onVariantSelect, onAssignToGeneral]
  )

  const handleReject = useCallback((): void => {
    void onClearSuggestion?.(url)
  }, [url, onClearSuggestion])

  const renderSuggestion = useCallback(
    ({ item }: { item: PickerItem }): ReactNode => {
      if (item.kind === 'tag') {
        return (
          <div className="flex items-center gap-2">
            <TagChip tag={item.tag} />
          </div>
        )
      }
      if (item.kind === 'general') {
        return (
          <div className="flex items-center gap-2 font-medium">
            <Globe className="size-4 text-muted-foreground" />
            <span>{siteWideLabel}</span>
          </div>
        )
      }
      const variant = item.variant
      return (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 space-y-0.5">
            <span
              className="block max-w-[18rem] truncate font-medium"
              title={variant.productName ?? variant.name}
            >
              {variant.productName ?? variant.name}
            </span>
            {variant.productName && variant.name !== variant.productName && (
              <span
                className="block max-w-[18rem] truncate text-xs text-muted-foreground"
                title={variant.name}
              >
                {variant.name}
              </span>
            )}
            <span
              className="block max-w-[18rem] truncate text-[11px] text-muted-foreground/60 font-mono"
              title={variant.variantSkuKey ?? variant.sku}
            >
              {variant.variantSkuKey ?? variant.sku}
            </span>
          </div>
          <div className="text-xs text-muted-foreground text-right min-w-[5rem]">
            {typeof variant.reportingCost === 'number' ? (
              <Money
                value={variant.reportingCost}
                currency={variant.reportingCurrency ?? variant.originalCurrency ?? undefined}
                minFraction={2}
                maxFraction={2}
              />
            ) : (
              '—'
            )}
          </div>
        </div>
      )
    },
    [siteWideLabel]
  )

  // Pre-select an AI-suggested variant so the user can confirm immediately.
  const initialSelectedItem = useMemo((): PickerItem | undefined => {
    if (url.suggestedVariantId && url.suggestedVariantName) {
      return toVariantPickerItem({
        id: url.suggestedVariantId,
        name: url.suggestedVariantName,
        productName: url.suggestedVariantName,
        variantSkuKey: url.suggestedVariantSku ?? undefined,
      } as Variant)
    }
    return undefined
  }, [url.suggestedVariantId, url.suggestedVariantName, url.suggestedVariantSku])

  // For non-AI rows showing an existing variant assignment, reflect the SKU as the label.
  const initialLabel = !initialSelectedItem ? (url.variantSku ?? undefined) : undefined

  const addonIcon = hasSuggestion ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <Sparkles className="size-4 text-muted-foreground/50" />
      </TooltipTrigger>
      <TooltipContent side="left">
        <p className="text-xs">{aiSuggestedMatchLabel}</p>
      </TooltipContent>
    </Tooltip>
  ) : undefined

  return (
    <div className="relative flex w-full items-center justify-end">
      <LookupConfirmField<PickerItem>
        fetchResults={fetchResults}
        onConfirm={handleConfirm}
        onReject={hasSuggestion && onClearSuggestion ? handleReject : undefined}
        renderSuggestion={renderSuggestion}
        getItemLabel={getItemLabel}
        placeholder={variantLabel}
        disabled={isLoading}
        minSearchLength={isCollectionTarget ? 0 : 2}
        maxVisibleResults={10}
        confirmAriaLabel={confirmAriaLabel}
        rejectAriaLabel={rejectAriaLabel}
        confirmIcon={confirmIcon}
        addonIcon={addonIcon}
        queryKeyBase={['url-assignment', 'search', allowGeneral ? 'with-general' : 'no-general']}
        className={cn(
          'flex-1 max-w-[340px]',
          hasSuggestion && 'ring-1 ring-muted-foreground/20 rounded-md'
        )}
        inputClassName="text-[12px] leading-5 placeholder:text-[12px]"
        initialLabel={initialLabel}
        initialSelectedItem={initialSelectedItem}
        clearOnBlur={false}
      />
    </div>
  )
})

export default UrlAssignmentCell
